import { z } from 'zod';
import { LOT_NO_REGEX } from './constants.js';
import {
  CLAIM_TYPES,
  COST_COMPONENT_KINDS,
  DECLARATION_TYPES,
  INCOTERMS,
  LOCALES,
  MARKETS,
  ORDER_STATUSES,
  PRODUCT_CATEGORIES,
  PROFILE_KINDS,
  ROLES,
  SHIPPING_MODES,
  TENANT_TYPES,
  VEHICLE_TYPES,
} from './enums.js';

/** z.enum no readonly const masīva (zod prasa mutējamu tuple — drošs cast). */
const zEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values as unknown as [T[number], ...T[number][]]);

export const marketSchema = zEnum(MARKETS);
export const tenantTypeSchema = zEnum(TENANT_TYPES);
export const localeSchema = zEnum(LOCALES);
export const roleSchema = zEnum(ROLES);
export const productCategorySchema = zEnum(PRODUCT_CATEGORIES);
export const orderStatusSchema = zEnum(ORDER_STATUSES);
export const incotermSchema = zEnum(INCOTERMS);
export const declarationTypeSchema = zEnum(DECLARATION_TYPES);
export const claimTypeSchema = zEnum(CLAIM_TYPES);
export const shippingModeSchema = zEnum(SHIPPING_MODES);
export const costComponentKindSchema = zEnum(COST_COMPONENT_KINDS);
export const profileKindSchema = zEnum(PROFILE_KINDS);
export const vehicleTypeSchema = zEnum(VEHICLE_TYPES);

export const lotNoSchema = z.string().regex(LOT_NO_REGEX, 'Lot Nr. formātam jābūt YYYY-MM-DD-XXX');

export const emailSchema = z.string().email().max(254);
export const passwordSchema = z.string().min(12, 'Parolei jābūt vismaz 12 rakstzīmes').max(200);

// --- Auth DTO (S2) ---
export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  legalName: z.string().min(1).max(200),
  country: z.string().length(2),
  market: marketSchema,
  locale: localeSchema.default('en'),
  turnstileToken: z.string().min(1),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  turnstileToken: z.string().optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const totpVerifySchema = z.object({
  code: z.string().regex(/^\d{6}$/, '6 ciparu kods'),
});

/** 6 ciparu TOTP VAI rezerves kods `XXXX-XXXX` (servera puse normalizē un izšķir). */
export const mfaCodeSchema = z.object({
  code: z.string().trim().min(6).max(16),
});

// --- Order DTO (S4) ---
export const orderLineInputSchema = z.object({
  productId: z.string().uuid(),
  qty: z.number().positive(),
  unit: z.string().default('kg'),
});

export const createOrderSchema = z.object({
  customerId: z.string().uuid(),
  market: marketSchema,
  requestedDate: z.string().datetime().optional(),
  shipToAddressId: z.string().uuid().optional(),
  notes: z.string().max(2000).optional(),
  lines: z.array(orderLineInputSchema).min(1, 'Vismaz viena rinda'),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

/** Piegādātāja apstiprinājums (datums + iekraušanas adrese — §1, S4). */
export const supplierConfirmSchema = z.object({
  confirmedDeliveryDate: z.string().datetime(),
  loadingAddressId: z.string().uuid(),
});
export type SupplierConfirmInput = z.infer<typeof supplierConfirmSchema>;

// --- Product DTO (S3) ---
export const createProductSchema = z.object({
  category: productCategorySchema,
  sku: z.string().min(1).max(64),
  namesI18n: z.record(localeSchema, z.string()),
  unit: z.string().default('kg'),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;
